import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Header, Layout, NextButton, Overview } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { NotificationBox, RadioButton, RadioGroup, Stack, Text } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { DeceasedFlowMonthlyInstallmentPaymentMethodStepRendering } from '@sitecore/types/DeceasedFlowMonthlyInstallmentPaymentMethodStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextDeceased } from '../../context';

type RadioItemOption = {
  name: string;
  label: string;
};

export const MonthlyInstallmentPaymentMethodStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextDeceased>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const changePaymentMethod = useFlowSelector(state => state.context.changePaymentMethod);
  const deceasedCustomer = useFlowSelector(state => state.context.deceasedCustomer);

  const { DeceasedFlowMonthlyInstallmentPaymentMethodStep: { fields: textualData } = {} } = usePlaceholderContent<{
    DeceasedFlowMonthlyInstallmentPaymentMethodStep: DeceasedFlowMonthlyInstallmentPaymentMethodStepRendering;
  }>();

  const salutationOptions: RadioItemOption[] = textualData?.paymentDetails.changePaymentMethodRadio.value.options ?? [];

  const formSchema = yup.object({
    changePaymentMethod: yup
      .string()
      .required(textualData?.paymentDetails?.changePaymentMethodRadio?.value?.requiredMessage),
  });

  const formMethods = useForm({
    mode: 'onBlur',
    defaultValues: {
      changePaymentMethod: changePaymentMethod ? 'true' : 'false',
    },
    resolver: typedYupResolver<{ changePaymentMethod: string }>(formSchema),
  });

  const {
    handleSubmit,
    watch,
    control,
    formState: { errors, isSubmitSuccessful },
  } = formMethods;

  const selectedChangePaymentMethod = watch('changePaymentMethod');

  useEffect(() => {
    if (isSubmitSuccessful) {
      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          changePaymentMethod: Boolean(selectedChangePaymentMethod === 'true'),
        },
      });
    }
  }, [isSubmitSuccessful, selectedChangePaymentMethod, sendToFlowMachine]);

  const maskBankAccount = (accountNumber: string | null | undefined) => {
    if (!accountNumber) return '';
    const lastThreeChars = accountNumber.slice(-3);
    return `** ** **** *** ** ${lastThreeChars}`;
  };

  return (
    <Layout.Content variant="B" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && <Header.Text>{textualData.content.text.value}</Header.Text>}
      </Header>
      <Overview>
        <Overview.Header>{textualData?.currentPaymentDetailsCard?.title?.value}</Overview.Header>
        <Overview.Details
          type="contact"
          title={textualData?.currentPaymentDetailsCard?.paymentDetailsLabel?.value ?? ''}>
          <Text as="p">
            {textualData?.currentPaymentDetailsCard?.paymentMethodLabel?.value} : {deceasedCustomer?.paymentMethod}
          </Text>
          <Text as="p">
            {textualData?.currentPaymentDetailsCard?.bankAccountNumberLabel?.value} :{' '}
            {maskBankAccount(deceasedCustomer?.bankAccountNumber)}
          </Text>
        </Overview.Details>
      </Overview>
      <Stack.Item>
        <Text size="BodyS">{textualData?.paymentDetails?.incorrectPaymentDetailsContent?.value}</Text>
      </Stack.Item>
      <Stack.Item>
        <Controller
          name="changePaymentMethod"
          control={control}
          render={({ field: { value, onChange, name } }) => (
            <RadioGroup
              error={errors?.changePaymentMethod?.message}
              direction="row"
              label={textualData?.paymentDetails?.changePaymentMethodRadio.value?.label ?? ''}
              name={name}
              value={value?.toLowerCase()}
              onValueChange={onChange}>
              {salutationOptions.map((option: RadioItemOption) =>
                option ? (
                  <RadioButton key={option.name} value={option.name}>
                    {option.label}
                  </RadioButton>
                ) : null,
              )}
            </RadioGroup>
          )}
        />
      </Stack.Item>
      {selectedChangePaymentMethod === 'true' && (
        <Stack.Item>
          <NotificationBox
            isAlert={false}
            text={<RichText html={textualData?.paymentDetails?.nonBlockingChoiceNotification?.value.content ?? ''} />}
            title={textualData?.paymentDetails?.nonBlockingChoiceNotification?.value?.title}
            variant={textualData?.paymentDetails?.nonBlockingChoiceNotification?.value?.variant}
          />
        </Stack.Item>
      )}

      <Stack.Item>
        <NextButton isLoading={isSubmitSuccessful} alignX="start">
          {textualData?.content.nextStepText?.value}
        </NextButton>
      </Stack.Item>
    </Layout.Content>
  );
};
